import React from 'react';
import { Link } from 'react-router-dom';
import { Scale, ArrowLeft, AlertTriangle } from 'lucide-react';

const TERMS_VERSION = '0.1 (DRAFT)';
const EFFECTIVE_DATE = 'To be confirmed at launch';
const COMPANY = 'Rostery';
const CONTACT_EMAIL = 'legal@skillwisedisability.com.au';

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-4xl mx-auto px-6 py-10">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <Link to="/login" className="flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
            <ArrowLeft size={15} /> Back to login
          </Link>
        </div>

        <div className="bg-white rounded-2xl border border-slate-200 p-8 shadow-sm space-y-8">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 rounded-xl bg-indigo-600 flex items-center justify-center shrink-0">
              <Scale size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Terms of Service</h1>
              <p className="text-sm text-slate-500 mt-1">Version {TERMS_VERSION} · Effective {EFFECTIVE_DATE}</p>
            </div>
          </div>

          {/* Draft notice */}
          <div className="flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4">
            <AlertTriangle size={18} className="text-amber-600 shrink-0 mt-0.5" />
            <p className="text-sm text-amber-800 leading-relaxed">
              <strong>DRAFT — review with legal counsel before launch.</strong> The content below is
              placeholder structure only. Each section must be reviewed and finalised by a qualified
              legal professional before these terms are relied upon.
            </p>
          </div>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">1. Acceptance of Terms</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              By accessing or using Rostery (the "Service"), operated by {COMPANY} ("we", "us", "our"),
              you ("the Customer", "you") agree to be bound by these Terms of Service. If you are
              entering into these terms on behalf of an organisation, you represent that you have the
              authority to bind that organisation. <em>[Placeholder — confirm acceptance mechanism,
              account registration flow, and minimum-age requirements.]</em>
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">2. Accounts &amp; Registration</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              You are responsible for maintaining the confidentiality of your account credentials and
              for all activity that occurs under your account. You must provide accurate information
              and keep it up to date. <em>[Placeholder — confirm per-organisation account model, user
              roles (admin, office user, carer), and credential-security obligations.]</em>
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">3. Acceptable Use</h2>
            <ul className="text-sm text-slate-600 space-y-1 list-disc pl-5">
              <li>You will not use the Service for any unlawful purpose or in breach of NDIS obligations.</li>
              <li>You will not attempt to gain unauthorised access to any part of the Service or other organisations' data.</li>
              <li>You will not upload malicious code or interfere with the integrity or performance of the Service.</li>
              <li>You will not resell, sublicense, or misrepresent the Service. <em>[Placeholder — confirm full prohibited-use list.]</em></li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">4. NDIS &amp; Service-Provider Responsibilities</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Rostery is a software tool that supports the administration of disability support services.
              You, as an NDIS-registered or unregistered service provider, remain solely responsible for
              the care you deliver, for compliance with the NDIS Practice Standards and Code of Conduct,
              for the accuracy of clinical and billing records you enter, and for your reportable-incident
              and claiming obligations. The Service does not provide care, clinical, financial, or legal
              advice. <em>[Placeholder — confirm scope of provider responsibility and any disclaimers
              specific to NDIS claiming and reporting.]</em>
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">5. Data &amp; Privacy</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Our handling of personal information is governed by our{' '}
              <Link to="/privacy-policy" className="text-indigo-600 hover:underline">Privacy Policy</Link>,
              which forms part of these terms. You are responsible for ensuring you have the necessary
              consents to enter participant, staff, and family data into the Service.
              <em> [Placeholder — confirm data ownership, data-processing roles, breach notification,
              and export/deletion on termination.]</em>
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">6. Availability &amp; Support</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              We aim to keep the Service available and to provide support during business hours, but we
              do not guarantee uninterrupted availability. Scheduled maintenance and unplanned outages
              may occur. <em>[Placeholder — confirm uptime commitment / SLA, support channels, and
              response targets, if any.]</em>
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">7. Limitation of Liability</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              To the maximum extent permitted by law, and subject to any rights you have under the
              Australian Consumer Law that cannot be excluded, the Service is provided "as is" and our
              liability is limited as set out in these terms. <em>[Placeholder — confirm liability cap,
              exclusion of indirect/consequential loss, and Australian Consumer Law carve-outs with
              legal counsel.]</em>
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">8. Termination</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              Either party may terminate in accordance with the agreed subscription terms. On
              termination, your access to the Service will cease and data will be handled as described
              in our data-retention and Privacy Policy provisions. <em>[Placeholder — confirm notice
              periods, suspension rights, and post-termination data export window.]</em>
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">9. Changes to These Terms</h2>
            <p className="text-sm text-slate-600 leading-relaxed">
              We may update these terms from time to time. We will notify you of material changes and,
              where required, seek your acceptance. Continued use of the Service after changes take
              effect constitutes acceptance. <em>[Placeholder — confirm notification method and notice
              period for changes.]</em>
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-lg font-semibold text-slate-800">10. Contact Us</h2>
            <div className="text-sm text-slate-600 space-y-1">
              <p><strong>Legal &amp; Compliance</strong></p>
              <p>{COMPANY}</p>
              <p>Email: <a href={`mailto:${CONTACT_EMAIL}`} className="text-indigo-600 hover:underline">{CONTACT_EMAIL}</a></p>
            </div>
          </section>

          <div className="pt-4 border-t border-slate-100 text-xs text-slate-400">
            DRAFT — version {TERMS_VERSION}. This document is a structural placeholder and must be
            reviewed with legal counsel before launch.
          </div>
        </div>
      </div>
    </div>
  );
}
